import React, { useEffect, useRef, useState } from 'react';
import { getPrivateOfferReviewQueue } from '../../api/private_offers';
import PrivateOfferReview from './PrivateOfferReview';
import './PrivateOffers.scss';

const VIEWS = [
  ['pending_review', 'Under review'],
  ['published', 'Approved'],
  ['revision_requested', 'Revision Requested'],
  ['all', 'All offers']
];

const PrivateOffers = () => {
  const [view, setView] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState({ view: 'pending_review', search: '', offset: 0, refresh: 0 });
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const listEnd = useRef(null);
  const resetOffers = () => {
    setQuery(previous => ({ view, search, offset: 0, refresh: previous.refresh + 1 }));
  };
  const handleReviewed = updated => {
    setPage(previous => ({
      ...previous,
      offers: previous.offers.map(offer => offer.id === updated.id ? { ...offer, ...updated } : offer)
    }));
    if (query.view !== 'all' && updated.publicationStatus !== query.view) {
      setPage(previous => ({ ...previous, offers: previous.offers.filter(offer => offer.id !== updated.id) }));
      setQuery(previous => ({ ...previous, offset: Math.max(0, previous.offset - 1), refresh: previous.refresh + 1 }));
    }
  };
  useEffect(() => {
    if (search === query.search && view === query.view) return;
    const timer = setTimeout(() => setQuery(previous => ({ ...previous, view, search, offset: 0 })), search === query.search ? 0 : 300);
    return () => clearTimeout(timer);
  }, [view, search, query.search, query.view]);

  useEffect(() => {
    const { view, search, offset } = query;
    let active = true;
    setLoading(true);
    setError('');
    getPrivateOfferReviewQueue({ publicationStatus: view, search, offset, limit: 25 })
      .then(result => {
        if (active) {
          setPage(previous => ({
            ...result,
            offers: offset
              ? [...(previous?.offers || []), ...result.offers.filter(offer => !previous?.offers.some(row => row.id === offer.id))]
              : result.offers
          }));
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError('Could not load offers. Please try again.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [query]);

  useEffect(() => {
    if (!listEnd.current || loading || error || search !== query.search || view !== query.view || !page?.pageInfo.hasMore) return;
    let requested = false;
    const observer = new IntersectionObserver(
      entries => {
        if (!requested && entries.some(entry => entry.isIntersecting)) {
          requested = true;
          setQuery(previous => ({ ...previous, offset: page.pageInfo.nextOffset }));
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(listEnd.current);
    return () => {
      requested = true;
      observer.disconnect();
    };
  }, [page, loading, error, search, view, query]);

  return (
    <main className='private-offers'>
      <header className='private-offers-header'>
        <div>
          <h1 className='private-offers-title'>Private Offers</h1>
          <p className='private-offers-description'>Review offer value and publish.</p>
        </div>
        <button className='private-offers-button' onClick={resetOffers}>Refresh</button>
      </header>
      <nav className='private-offers-tabs' aria-label='Review status'>
        {VIEWS.map(([value, label]) => (
          <button
            className='private-offers-tab'
            key={value}
            aria-current={view === value ? 'page' : undefined}
            onClick={() => setView(value)}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className='review-filters'>
        <input
          className='private-offers-search'
          aria-label='Search offers'
          placeholder='Brand or offer starts with…'
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
        <span>{page ? `${page.pageInfo.totalCount} offers` : 'Loading…'}</span>
      </div>
      {error && (
        <p className='private-offers-error' role='alert'>
          {error} <button className='private-offers-button' onClick={() => setQuery(previous => ({ ...previous, refresh: previous.refresh + 1 }))}>Try again</button>
        </p>
      )}
      <div className='review-table-scroll'>
        <table className='private-offers-table' aria-busy={loading} aria-label='Private offer review queue'>
          <thead>
            <tr>
              {[
                'Submitted',
                'Brand',
                'Offer value',
                'Audience',
                'Claim window',
                ...(view === 'published' ? ['Offer status'] : []),
                'Quality score',
                'Decision'
              ].map(label => (
                <th className='private-offers-column' key={label} scope='col'>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          {page?.offers.map(offer => (
            <PrivateOfferReview
              key={`${offer.id}:${offer.reviewRevision}`}
              offer={offer}
              showOfferStatus={view === 'published'}
              onReviewed={handleReviewed}
            />
          ))}
          {!page?.offers.length && (
            <tbody>
              <tr>
                <td className='private-offers-empty' colSpan={view === 'published' ? 8 : 7}>{loading ? 'Loading offers…' : error ? 'No offers loaded.' : 'No offers match. Try another status or clear your search.'}</td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      <footer className='private-offers-footer' ref={listEnd} role='status'>
        {loading && page ? query.offset ? 'Loading more offers…' : 'Updating offers…' : page ? `${page.offers.length} of ${page.pageInfo.totalCount} offers` : ''}
      </footer>
    </main>
  );
};
export default PrivateOffers;
